import React, {Component} from "react";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import Dictation from "./Dictation";
import RecommenderService from "../../../../services/recommender-service/RecommenderService";

/**
 * The review strip at the foot of the CoPilot drawer: one verdict on whatever the
 * panel just produced — the parse on Today's Visit, the findings on EMR Check, the
 * code list on CPT Recommender.
 *
 * Thumb first, words optional. 👍 posts immediately (a good answer should cost the
 * clinician one click); 👎 opens the comment box, because "wrong" is only useful with
 * the reason attached. The box takes dictation, since the clinician's hands are
 * already busy — same mic as the note itself.
 *
 * The panel supplies `getSnapshot()`, called at click time rather than on every render:
 * it walks the whole analysis/compose/recommendation state and there is no reason to
 * pay for that until someone actually has an opinion.
 */
export default class FeedbackBar extends Component {

    state = {rating: null, comment: "", sending: false, sent: false, error: null};

    componentDidUpdate(prevProps) {
        // A new answer is a new thing to judge: retract the "thanks" so the strip
        // invites a verdict on what is on screen now, not on what was there before.
        if (prevProps.resetKey !== this.props.resetKey && (this.state.sent || this.state.rating)) {
            this.setState({rating: null, comment: "", sent: false, error: null});
        }
    }

    choose = (rating) => {
        if (rating === "down") {
            this.setState({rating, sent: false, error: null});
            return;
        }
        this.setState({rating, sent: false, error: null}, () => this.send(rating));
    };

    appendDictated = (chunk) => {
        if (!chunk) return;
        this.setState(state => {
            const separator = !state.comment || /\s$/.test(state.comment) ? "" : " ";
            return {comment: state.comment + separator + chunk};
        });
    };

    send = (rating = this.state.rating) => {
        if (!rating || this.state.sending) return;
        const snapshot = this.props.getSnapshot ? this.props.getSnapshot() : {};

        this.setState({sending: true, error: null});
        RecommenderService.feedback(
            {
                rating,
                comment: this.state.comment.trim(),
                surface: this.props.surface,
                visitId: this.props.visitId,
                mrn: this.props.mrn,
                clinic: this.props.clinic,
                specialty: this.props.specialty,
                user: this.props.user,
                request_ids: RecommenderService.requestIds(),
                snapshot,
                app_version: process.env.REACT_APP_VERSION,
                client_ts: new Date().toISOString(),
            },
            () => this.setState({sending: false, sent: true, comment: ""}),
            () => this.setState({sending: false, error: "Could not send — try again."}),
        );
    };

    render() {
        const {rating, comment, sending, sent, error} = this.state;
        const open = rating === "down";

        if (sent) {
            return (
                <div className="emr-check-feedback">
                    <div className="emr-check-feedback-row">
                        <span className="emr-check-feedback-done">✓ Thanks — logged with this answer.</span>
                        <button type="button" className="emr-check-feedback-link"
                                onClick={() => this.setState({rating: "down", sent: false})}>
                            Say more
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className={`emr-check-feedback ${open ? "open" : ""}`}>
                <div className="emr-check-feedback-row">
                    <span className="emr-check-feedback-ask">Was this useful?</span>
                    <button type="button"
                            className={`emr-check-feedback-thumb up ${rating === "up" ? "on" : ""}`}
                            disabled={sending} title="This was right" aria-label="Useful"
                            onClick={() => this.choose("up")}>
                        <FontAwesomeIcon icon={["fas", "thumbs-up"]}/>
                    </button>
                    <button type="button"
                            className={`emr-check-feedback-thumb down ${open ? "on" : ""}`}
                            disabled={sending} title="This was wrong or unhelpful" aria-label="Not useful"
                            onClick={() => this.choose("down")}>
                        <FontAwesomeIcon icon={["fas", "thumbs-down"]}/>
                    </button>
                    {!open &&
                        <button type="button" className="emr-check-feedback-link"
                                onClick={() => this.setState({rating: "down"})}>
                            Add a comment
                        </button>}
                    {error && <span className="emr-check-feedback-error">{error}</span>}
                </div>

                {open &&
                    <div className="emr-check-feedback-detail">
                        {/* Same wrap as the note box, so the mic sits inside the field it
                            dictates into rather than floating off in the button row. */}
                        <div className="emr-check-draft-wrap">
                            <textarea className="emr-check-feedback-text"
                                      placeholder="What was wrong? The finding, the wording, the code…"
                                      value={comment} autoFocus
                                      onChange={e => this.setState({comment: e.target.value})}/>
                            <Dictation onAppend={this.appendDictated}/>
                        </div>
                        {/* Left-aligned on purpose: the CoPilot's round close button floats over
                            the drawer's bottom-right corner, and a Send button under it cannot
                            be clicked. */}
                        <div className="emr-check-feedback-actions">
                            <Button size="sm" variant="primary" disabled={sending}
                                    onClick={() => this.send()}>
                                {sending ? "Sending…" : "Send"}
                            </Button>
                            <button type="button" className="emr-check-feedback-link"
                                    onClick={() => this.setState({rating: null, comment: ""})}>
                                Cancel
                            </button>
                        </div>
                    </div>}
            </div>
        );
    }
}
